import { Client } from 'colyseus.js';

const GAME_SERVER_URL = (() => {
  const base =
    import.meta.env?.VITE_GAME_SERVER_URL ??
    (typeof window !== 'undefined'
      ? `${window.location.protocol === 'https:' ? 'https:' : 'http:'}//${window.location.hostname}:2567`
      : 'http://localhost:2567');
  return base.replace(/\/+$/, '');
})();

let client: Client | null = null;

export function getGameClient(): Client {
  if (!client) {
    client = new Client(GAME_SERVER_URL);
  }
  return client;
}

export interface TableConfig {
  id: string;
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  maxSeats: number;
  stakes: string;
  players?: number;
}

export interface CreateTableConfig {
  maxSeats: 2 | 6;
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
}

export async function createTable(
  token: string,
  config: CreateTableConfig
): Promise<{ tableId: string } & TableConfig> {
  const res = await fetch(`${GAME_SERVER_URL}/api/create-table`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? 'Failed to create table');
  }
  return res.json();
}

export async function fetchTables(): Promise<TableConfig[]> {
  const res = await fetch(`${GAME_SERVER_URL}/api/tables`);
  if (!res.ok) throw new Error('Failed to fetch tables');
  return res.json();
}

export async function checkUsernameAvailability(
  username: string
): Promise<{ available: boolean; error?: string }> {
  const trimmed = username.trim().slice(0, 10);
  const res = await fetch(
    `${GAME_SERVER_URL}/api/check-username?username=${encodeURIComponent(trimmed)}`
  );
  let data: { available?: boolean; error?: string };
  try {
    const ct = res.headers.get('content-type') ?? '';
    data = ct.includes('application/json') ? await res.json() : {};
  } catch {
    data = {};
  }
  if (!res.ok) return { available: false, error: data?.error ?? `Request failed (${res.status})` };
  return { available: data?.available ?? false, error: data?.error };
}

export interface MeResponse {
  balance: number;
  username: string | null;
}

export async function fetchMe(token: string): Promise<MeResponse> {
  const res = await fetch(`${GAME_SERVER_URL}/api/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = new Error('Failed to fetch profile') as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function registerProfile(token: string, username: string): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${GAME_SERVER_URL}/api/register-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, username: username.trim().slice(0, 10) }),
  });
  let data: { error?: string };
  try {
    const ct = res.headers.get('content-type') ?? '';
    data = ct.includes('application/json') ? await res.json() : {};
  } catch {
    data = {};
  }
  if (!res.ok) return { ok: false, error: data?.error ?? `Request failed (${res.status})` };
  return { ok: true };
}

export type { Client };
